import { Component, computed, effect, forwardRef, input, model, OnDestroy, OnInit, output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, Validator } from '@angular/forms';
import { FormCustomIdentification, FormIdentification, IdentificationType, SelectedIdentification } from './models/identification';
import { Subscription } from 'rxjs';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { CheckboxComponent } from 'src/app/controls/components/basic/checkbox/checkbox.component';
import { LinkComponent } from 'src/app/controls/components/basic/link/link.component';

import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { ItemStatus, NewCommentEvent, PartnerConversation, PartnerOnboardingComment, ToggleConversationEvent } from '../../models/partner-onboarding';
import { OnboardingCommentsIconComponent } from '../onboarding-comments-icon/onboarding-comments-icon.component';
import { OnboardingCommentsContainerComponent } from '../onboarding-comments-container/onboarding-comments-container.component';
import { ConversationComponent } from 'src/app/controls/components/advanced/conversation/conversation.component';
import { Conversation, ConversationMessage } from 'src/app/controls/components/advanced/conversation/models/conversation';

@Component({
  selector: 'app-onboarding-identifications',
  standalone: true,
  imports: [FormsModule, LayoutRowComponent, LayoutColumnComponent, TextboxComponent, CheckboxComponent, LinkComponent, OnboardingCommentsIconComponent, ConversationComponent, OnboardingCommentsContainerComponent],
  templateUrl: './onboarding-identifications.component.html',
  styleUrl: './onboarding-identifications.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => OnboardingIdentificationsComponent),
        multi: true
      },
      {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => OnboardingIdentificationsComponent),
        multi: true
      }
    ],
})
export class OnboardingIdentificationsComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  form = viewChild<NgForm>('form');
  model = model<SelectedIdentification[]>([]);
  readonly = input<boolean>(false);
  possibleIdentificationTypes = input.required<IdentificationType[]>();
  allowConversation = input<boolean>(true);
  readonlyConversation = input<boolean>(false);
  conversation = input<PartnerConversation | null>(null);
  unreadConversationIds = input<string[]>([]);
  autoRefreshEnabled = input<boolean>(false);
  toggleAutoRefreshClicked = output<void>();
  toggleConversationClicked = output<ToggleConversationEvent>();
  newCommentClicked = output<NewCommentEvent>();
  formStatusChanged = output<string>(); // emits: 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'
  formChangesSubscription: Subscription;

  constructor() {
    effect((onCleanup) => {
        const frm = this.form()?.form;
        if (!frm) return;
    
        // emit current status once and on subsequent changes
        this.formStatusChanged.emit(frm.status);
        const sub = frm.statusChanges.subscribe(status => this.formStatusChanged.emit(status));

        onCleanup(() => sub.unsubscribe());
    });
  }
  
  ngOnInit(): void {
    this.formChangesSubscription = this.form().form.valueChanges.subscribe(x => {
      this.onChanged(this.getSelectedIdentifications(x));
    })
  }


  // Computed signals for form fields

  formIdentifications = computed(() => this.possibleIdentificationTypes()?.map(i => {
    const value = this.model()?.find(x => x.IdentificationType.Id === i.Id)?.Value;
    const status = this.model()?.find(x => x.IdentificationType.Id === i.Id)?.Status ?? {Status: ItemStatus.Open, StatusMessage: '', CreatedEntities: []};
    const conversationId = this.model()?.find(x => x.IdentificationType.Id === i.Id)?.ConversationId ?? null
    return new FormIdentification({IdentificationType: i, IdentificationValue: value, Selected: !!value, Status: status, ConversationId: conversationId});
  }));
  newFormCustomIdentifications= signal<FormCustomIdentification[]>([]);
  formCustomIdentifications = computed(() => {
    const existingCustomIdentifications = this.model()?.filter(i => i.IdentificationType?.Id === null).map(i => {
      const value = i.Value;
      const status = i.Status ?? {Status: ItemStatus.Open, StatusMessage: '', CreatedEntities: []};
      const conversationId = i.ConversationId
      return new FormCustomIdentification({IdentificationType: i.IdentificationType.Name, IdentificationValue: value, Status: status, ConversationId: conversationId});
    }) ?? [];
    const newCustomIdentifications = this.newFormCustomIdentifications();
    return existingCustomIdentifications.concat(newCustomIdentifications) ?? [];
  });
  conversationToShow = computed(() => {
    if (!this.conversation()) return null;
    return new Conversation(
      {
        Id: this.conversation().Id, 
        UserId: 'partner',
        Comments: this.conversation().Comments?.map(cm => {
          const userId = cm.IsAction ? 'system' : (cm.FromCustomer ? 'customer' : 'partner');
          return new ConversationMessage({UserId: userId, Message: cm.Message, Timestamp: cm.Timestamp})
      }) ?? [], 
        LastRead: this.conversation().PartnerLastRead
      }
    );
  });


  // Form methods

  addCustomIdentification() {
    this.newFormCustomIdentifications.update(i => [...i, new FormCustomIdentification({IdentificationType: null, IdentificationValue: null, Status: {Status: ItemStatus.Open, CreatedEntities: []}, ConversationId: null})]);
  }

  removeCustomIdentification(idx: number) {
    const existingIdentifications = this.model()?.filter(i => i.IdentificationType?.Id !== null) ?? [];
    const existingCustomIdentifications = this.model()?.filter(i => i.IdentificationType?.Id === null) ?? [];

    if (idx < existingCustomIdentifications.length) {
      const remainingCustomIdentifications = existingCustomIdentifications.slice(0, idx).concat(existingCustomIdentifications.slice(idx + 1));
      this.model.set(existingIdentifications.concat(remainingCustomIdentifications));
    }
    else {      
      this.newFormCustomIdentifications.update(identifications => {
        const newIdentificationsIdx = idx - existingCustomIdentifications.length;
        identifications.splice(newIdentificationsIdx, 1);
        return [...identifications];
      });
    }
  }


  // Field state methods

  isDisabled(field: FormIdentification) {
    const checkboxValue = this.form().form.get('selected-' + field.IdentificationType.Id)?.getRawValue() as boolean ?? false;
    return !checkboxValue;
  }

  isReadOnly(field: FormIdentification | FormCustomIdentification) {
    if (field.Status.Status === ItemStatus.Accepted || field.Status.Status === ItemStatus.AwaitingAcceptance) {
      return true;
    } 
    if (this.readonly()) {
      if (field.Status.Status === ItemStatus.Open) {
        return (field as FormIdentification)?.Selected===false;
      }
      else {
        return true;
      }
    }
    return false;
  }


  // Get selected identifications from form value

  getSelectedIdentifications(formValue: unknown) {
    const identifications = this.formIdentifications().filter(i => {
      const selectedName = 'selected-' + i.IdentificationType.Id;
      return formValue[selectedName] as boolean ?? false;
    }).map(i => {
      const valueName = 'identificationValue-' + i.IdentificationType.Id;
      const type = i.IdentificationType;
      const value = formValue[valueName] as string;
      return new SelectedIdentification({IdentificationType: type, Value: value, Status: i.Status, ConversationId: i.ConversationId});
    });

    const customIdentifications = this.formCustomIdentifications().map((i, index) => {
      const typeName = 'customIdentificationType-' + index;
      const valueName = 'customIdentificationValue-' + index;
      const type = formValue[typeName] as string;
      const value = formValue[valueName] as string;
      return new SelectedIdentification({IdentificationType: {Id: null, Name: type}, Value: value, Status: i.Status, ConversationId: i.ConversationId});
    });

    return identifications.concat(customIdentifications);
  }


  // Conversation methods

  hasUnreadMessages(conversationId: string): boolean {
    if (!this.unreadConversationIds()) {
      return false;
    }
    return this.unreadConversationIds()?.some(id => id === conversationId);
  }

  hasConversationId(field: FormIdentification | FormCustomIdentification) {
    return !!field.ConversationId;
  }

  toggleConversation(conversationId: string, areaId: string) {
    this.toggleConversationClicked.emit(new ToggleConversationEvent({ConversationId: conversationId, AreaId: areaId}));
  }

  newComment(message: ConversationMessage, conversationId: string) {
    const comment = new PartnerOnboardingComment({FromCustomer: false, Message: message.Message, Timestamp: message.Timestamp});
    const newCommentEvent = new NewCommentEvent({Comment: comment, ConversationId: conversationId});
    this.newCommentClicked.emit(newCommentEvent);
  }

  toggleAutoRefresh(): void {
    this.toggleAutoRefreshClicked.emit();
  }


  // ControlValueAccessor interface methods

  writeValue(obj: SelectedIdentification[]): void {
    this.newFormCustomIdentifications.set([]);
    this.model.set(obj);
  }

  onChanged: (value: SelectedIdentification[]) => void = () => { };
  registerOnChange(fn: (value: SelectedIdentification[]) => void): void {
    this.onChanged = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form().form.disable();
    } 
    else {
      this.form().form.enable();
    }
  }


  // Validator interface methods

  validate() {
    return this.form().form.valid ? [] : ['Invalid'];
  }


  // OnInit interface methods

  ngOnDestroy(): void {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }
}
